import { LayoutGrid, List, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import type {
  PortfolioFilter,
  PortfolioSort,
  PortfolioView,
} from "@/utils/portfolioPrefs";

type PortfolioToolbarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  filter: PortfolioFilter;
  onFilterChange: (filter: PortfolioFilter) => void;
  sort: PortfolioSort;
  onSortChange: (sort: PortfolioSort) => void;
  view: PortfolioView;
  onViewChange: (view: PortfolioView) => void;
};

type FilterChip = {
  value: PortfolioFilter;
  label: string;
  inert?: boolean;
};

const FILTER_CHIPS: FilterChip[] = [
  { value: "toate", label: "Toate" },
  { value: "active", label: "Active" },
  { value: "arhivate", label: "Arhivate" },
  { value: "cu-alerte", label: "Cu alerte", inert: true },
];

export function PortfolioToolbar({
  search,
  onSearchChange,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  view,
  onViewChange,
}: PortfolioToolbarProps) {
  return (
    <div
      data-testid="portfolio-toolbar"
      className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
    >
      <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
        <div className="relative md:w-72">
          <Search
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-400"
          />
          <Input
            type="search"
            placeholder="Caută după nume sau CUI"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid="portfolio-search-input"
            aria-label="Caută companii după nume sau CUI"
            className="h-10 pl-9"
          />
        </div>

        <div
          role="tablist"
          aria-label="Filtre companii"
          className="flex flex-wrap items-center gap-2"
        >
          {FILTER_CHIPS.map((chip) => {
            const isActive = filter === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-disabled={chip.inert ? true : undefined}
                disabled={chip.inert}
                onClick={() => {
                  if (chip.inert) return;
                  onFilterChange(chip.value);
                }}
                data-testid={`portfolio-filter-${chip.value}`}
                data-inert={chip.inert ? "true" : "false"}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  isActive
                    ? "border-navy-900 bg-navy-900 text-white"
                    : "border-ds-border-200 bg-white text-ds-text-600 hover:border-ds-border-300",
                  chip.inert &&
                    "cursor-not-allowed opacity-50 hover:border-ds-border-200"
                )}
              >
                {chip.label}
                {chip.inert && (
                  <span className="text-[10px] uppercase tracking-wide text-ds-text-400">
                    curând
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Select
          value={sort}
          onValueChange={(value) => onSortChange(value as PortfolioSort)}
        >
          <SelectTrigger
            className="h-10 w-44"
            data-testid="portfolio-sort"
            aria-label="Sortează companii"
          >
            <SelectValue placeholder="Sortează" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="ultima-activitate"
              data-testid="portfolio-sort-option-ultima-activitate"
            >
              Ultima activitate
            </SelectItem>
            <SelectItem
              value="nume-az"
              data-testid="portfolio-sort-option-nume-az"
            >
              Nume A→Z
            </SelectItem>
          </SelectContent>
        </Select>

        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(value) => {
            if (value === "grid" || value === "list") onViewChange(value);
          }}
          aria-label="Mod afișare"
          className="rounded-md border border-ds-border-200 bg-white p-0.5"
        >
          <ToggleGroupItem
            value="grid"
            data-testid="portfolio-view-grid"
            aria-label="Afișează ca grilă"
            className="h-8 w-8 data-[state=on]:bg-navy-900 data-[state=on]:text-white"
          >
            <LayoutGrid className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem
            value="list"
            data-testid="portfolio-view-list"
            aria-label="Afișează ca listă"
            className="h-8 w-8 data-[state=on]:bg-navy-900 data-[state=on]:text-white"
          >
            <List className="h-4 w-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  );
}
